/**
 * Salon-booking theme palette + tiny shared helpers.
 *
 * Mirrors the structure of the older `BookingTheme.js` so a downstream
 * component can switch between the two themes without rewiring props.
 * The salon palette follows Nomio's brand system: blue-to-teal gradients
 * with teal accents instead of the old rose/magenta demo colors.
 *
 * Same dark-mode caveat as the rest of the project: `useTheme().isDarkMode`
 * is the OPPOSITE of visually-dark. Caller must pass `isVisuallyDark`.
 */

export function getSalonPalette(isVisuallyDark, brandColor, brandColorSoft) {
  const accent = brandColor || "hsl(166 100% 40%)";
  const accentSoft =
    brandColorSoft || "hsla(166, 100%, 40%, 0.14)";
  const brandGradient = "linear-gradient(135deg, hsl(214 82% 52%) 0%, hsl(166 100% 40%) 100%)";

  if (isVisuallyDark) {
    return {
      pageBg: "hsl(234 30% 13%)",
      surface: "hsl(234 30% 18%)",
      surfaceAlt: "hsl(234 28% 22%)",
      surfaceHover: "hsl(234 30% 24%)",
      border: "1px solid hsl(234 20% 28%)",
      borderStrong: "1px solid hsl(234 20% 36%)",
      text: "hsl(0 0% 96%)",
      textMuted: "hsl(0 0% 75%)",
      textSubtle: "hsl(0 0% 60%)",
      accent,
      accentSoft,
      accentBorder: accent,
      brandGradient,
      success: "hsl(140 65% 55%)",
      successSoft: "hsla(140, 65%, 55%, 0.15)",
      warning: "hsl(40 90% 60%)",
      danger: "hsl(0 75% 60%)",
      shadow: "0 8px 24px rgba(0, 0, 0, 0.35)",
      shadowSoft: "0 2px 10px rgba(0, 0, 0, 0.25)",
      heroOverlay:
        "linear-gradient(180deg, rgba(11, 16, 32, 0.15) 0%, rgba(11, 16, 32, 0.75) 100%)",
    };
  }
  return {
    pageBg: "hsl(20 30% 98%)",
    surface: "hsl(0 0% 100%)",
    surfaceAlt: "hsl(20 30% 98%)",
    surfaceHover: "hsl(20 25% 95%)",
    border: "1px solid hsl(20 20% 92%)",
    borderStrong: "1px solid hsl(20 25% 82%)",
    text: "hsl(234 30% 16%)",
    textMuted: "hsl(220 12% 42%)",
    textSubtle: "hsl(220 10% 58%)",
    accent,
    accentSoft,
    accentBorder: accent,
    brandGradient,
    success: "hsl(140 65% 38%)",
    successSoft: "hsla(140, 65%, 38%, 0.12)",
    warning: "hsl(38 95% 50%)",
    danger: "hsl(0 75% 50%)",
    shadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
    shadowSoft: "0 2px 10px rgba(15, 23, 42, 0.06)",
    heroOverlay:
      "linear-gradient(180deg, rgba(15, 23, 42, 0.15) 0%, rgba(15, 23, 42, 0.65) 100%)",
  };
}

/** Format an integer price with the salon's currency (default AMD). */
export function formatPrice(amount, currency = "AMD", lang = "hy") {
  if (amount == null) return "";
  const formatted = new Intl.NumberFormat(lang === "hy" ? "hy-AM" : "en-US").format(amount);
  if (currency === "AMD") {
    return lang === "hy" ? `${formatted} ֏` : `${formatted} AMD`;
  }
  return `${formatted} ${currency}`;
}

/** Format duration in minutes → "1 ժ 30 ր" / "1h 30m". */
export function formatDuration(minutes, lang = "hy") {
  if (!minutes) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (lang === "hy") {
    if (h && m) return `${h} ժ ${m} ր`;
    if (h) return `${h} ժ`;
    return `${m} ր`;
  }
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

/** Localised string picker for the mock-data `{ hy, en }` shape. */
export function pickLocalized(node, lang) {
  if (!node) return "";
  if (typeof node === "string") return node;
  if (node[lang]) return node[lang];
  return node.hy || node.en || "";
}
